import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  PlayArrow as ProcessIcon,
  Refresh as RefreshIcon,
  ArrowForward as NextIcon
} from '@mui/icons-material';
import { invoiceService } from '../../services/invoice.service';

/**
 * Processing Page Component
 * Shows synchronous document processing with progress and results
 */
const ProcessingPage = ({ documentId, onBack, onNext, onRefresh }) => {
  const [processingState, setProcessingState] = useState('idle'); // 'idle', 'processing', 'completed', 'error'
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [stepStatuses, setStepStatuses] = useState({}); // Track individual step statuses
  const [validationStatus, setValidationStatus] = useState(null); // Track validation results

  // Processing steps
  const processingSteps = [
    'Initializing Processing',
    'Extracting Text from PDF',
    'Running AI Data Extraction',
    'Validating Purchase Order',
    'Checking Business Rules',
    'Generating Results',
    'Finalizing Processing'
  ];

  // Load document info on mount
  useEffect(() => {
    loadDocumentInfo();
  }, [documentId]);

  const loadDocumentInfo = async () => {
    try {
      const response = await invoiceService.getDocumentMetadata(documentId);
      setDocumentInfo(response);
    } catch (error) {
      console.error('Failed to load document info:', error);
    }
  };

  const startProcessing = async () => {
    try {
      setProcessingState('processing');
      setProgress(0);
      setCurrentStep(0);
      setError(null);
      setResults(null);
      setStepStatuses({});
      setValidationStatus(null);

      // Simulate processing steps with progress updates
      const totalSteps = processingSteps.length;
      const stepDuration = 2000; // 2 seconds per step

      for (let i = 0; i < totalSteps; i++) {
        setCurrentStep(i);
        setProgress((i / totalSteps) * 100);
        
        // Mark previous steps as completed
        if (i > 0) {
          setStepStatuses(prev => ({
            ...prev,
            [i - 1]: 'completed'
          }));
        }
        
        // Simulate processing time
        await new Promise(resolve => setTimeout(resolve, stepDuration));
      }

      // Call the actual processing API with progress tracking
      const processingResult = await invoiceService.processDocumentSync(
        documentId,
        (progress, message) => {
          // Update progress during processing
          setProgress(progress);
        }
      );
      
      // Analyze the processing results to determine validation status
      const validationResult = analyzeValidationResults(processingResult);
      setValidationStatus(validationResult);
      
      // Update step statuses based on validation results
      const updatedStepStatuses = { ...stepStatuses };
      
      // Mark all steps as completed, but check for specific failures
      for (let i = 0; i < totalSteps; i++) {
        if (i === 2) { // "Running AI Data Extraction" step (index 2) - check confidence score
          updatedStepStatuses[i] = validationResult.confidenceScore >= 0.9 ? 'completed' : 'failed';
        } else if (i === 4) { // "Checking Business Rules" step (index 4) - check PO validation
          updatedStepStatuses[i] = validationResult.poValidationStatus === 'valid' ? 'completed' : 'failed';
        } else {
          updatedStepStatuses[i] = 'completed';
        }
      }
      
      setStepStatuses(updatedStepStatuses);
      
      // Complete the processing
      setCurrentStep(totalSteps - 1);
      setProgress(100);
      setResults(processingResult);
      setProcessingState('completed');

      // Refresh the document list to show updated status
      if (onRefresh) {
        onRefresh();
      }

    } catch (error) {
      console.error('Processing failed:', error);
      setError(error.message);
      setProcessingState('error');
    }
  };

  const handleNext = () => {
    if (onNext) {
      onNext(results);
    }
  };

  // Analyze validation results from processing response
  const analyzeValidationResults = (processingResult) => {
    try {
      const processingSummary = processingResult.processing_summary || {};
      
      // Check PO validation status from processing_summary
      const poValidationStatus = processingSummary.po_validation;
      const confidenceScore = parseFloat(processingSummary.confidence_score) || 0;
      
      let isValid = true;
      let validationMessage = 'All validations passed';
      let failedChecks = [];
      
      // Check confidence score (LLM Agent step - should be > 0.9)
      if (confidenceScore < 0.9) {
        isValid = false;
        failedChecks.push(`LLM Confidence (${(confidenceScore * 100).toFixed(1)}%)`);
      }
      
      // Check PO validation status
      if (poValidationStatus === 'invalid') {
        isValid = false;
        failedChecks.push('PO Validation');
      }
      
      // Update validation message based on results
      if (!isValid) {
        if (failedChecks.length === 1) {
          validationMessage = `Validation failed: ${failedChecks[0]}`;
        } else {
          validationMessage = `Validation failed: ${failedChecks.join(', ')}`;
        }
      }
      
      return {
        isValid,
        message: validationMessage,
        failedChecks,
        poValidationStatus,
        confidenceScore,
        processingSummary
      };
    } catch (error) {
      console.error('Error analyzing validation results:', error);
      return {
        isValid: true,
        message: 'Validation status unknown',
        failedChecks: [],
        poValidationStatus: 'unknown',
        confidenceScore: 0,
        processingSummary: null
      };
    }
  };

  const getStepIcon = (stepIndex) => {
    // Check if this step has a specific status (failed validation)
    if (stepStatuses[stepIndex] === 'failed') {
      return <ErrorIcon color="error" />;
    }
    
    if (processingState === 'error' && stepIndex === currentStep) {
      return <ErrorIcon color="error" />;
    }
    if (processingState === 'completed' || stepIndex < currentStep || stepStatuses[stepIndex] === 'completed') {
      return <SuccessIcon color="success" />;
    }
    if (stepIndex === currentStep && processingState === 'processing') {
      return <ProcessIcon color="primary" />;
    }
    return <InfoIcon color="disabled" />;
  };

  const getStepColor = (stepIndex) => {
    // Check if this step has a specific status (failed validation)
    if (stepStatuses[stepIndex] === 'failed') {
      return 'error';
    }
    
    if (processingState === 'error' && stepIndex === currentStep) {
      return 'error';
    }
    if (processingState === 'completed' || stepIndex < currentStep || stepStatuses[stepIndex] === 'completed') {
      return 'success';
    }
    if (stepIndex === currentStep && processingState === 'processing') {
      return 'primary';
    }
    return 'disabled';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Box flexGrow={1}>
          <Typography variant="h4" component="h1">
            Document Processing
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {documentInfo?.metadata?.original_filename || documentInfo?.document_name || 'Processing Document'}
          </Typography>
        </Box>
        {processingState === 'idle' && (
          <Button
            variant="contained"
            startIcon={<ProcessIcon />}
            onClick={startProcessing}
            size="large"
          >
            Start Processing
          </Button>
        )}
        {processingState === 'completed' && (
          <Button
            variant="contained"
            startIcon={<NextIcon />}
            onClick={handleNext}
            size="large"
            color="success"
          >
            Continue
          </Button>
        )}
        {processingState === 'error' && (
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={startProcessing}
            size="large"
            color="warning"
          >
            Retry
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Processing Steps */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Processing Steps
              </Typography>
              
              {processingState === 'processing' && (
                <Box mb={3}>
                  <LinearProgress 
                    variant="determinate" 
                    value={progress} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {Math.round(progress)}% Complete
                  </Typography>
                </Box>
              )}

              <Stepper activeStep={currentStep} orientation="vertical">
                {processingSteps.map((step, index) => (
                  <Step key={index} completed={index < currentStep}>
                    <StepLabel
                      StepIconComponent={() => getStepIcon(index)}
                      sx={{
                        '& .MuiStepLabel-label': {
                          color: getStepColor(index) === 'success' ? 'success.main' : 
                                 getStepColor(index) === 'error' ? 'error.main' :
                                 getStepColor(index) === 'primary' ? 'primary.main' : 'text.disabled'
                        }
                      }}
                    >
                      {step}
                    </StepLabel>
                    <StepContent>
                      {index === currentStep && processingState === 'processing' && (
                        <Box sx={{ mb: 2 }}>
                          <LinearProgress sx={{ width: '100%' }} />
                        </Box>
                      )}
                      {index < currentStep && stepStatuses[index] !== 'failed' && (
                        <Typography variant="body2" color="success.main">
                          ✓ Completed successfully
                        </Typography>
                      )}
                      {stepStatuses[index] === 'failed' && (
                        <Box>
                          <Typography variant="body2" color="error.main">
                            ✗ {index === 2 ? 'LLM Confidence too low' : 
                                index === 4 ? 'Business rules validation failed' : 
                                'Validation failed'}
                          </Typography>
                          {validationStatus && validationStatus.failedChecks.length > 0 && (
                            <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                              {index === 2 && validationStatus.confidenceScore < 0.9 ? 
                                `Confidence: ${(validationStatus.confidenceScore * 100).toFixed(1)}% (required: 90%)` :
                                index === 4 && validationStatus.poValidationStatus === 'invalid' ?
                                'PO validation failed' :
                                `Failed checks: ${validationStatus.failedChecks.join(', ')}`}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {index === currentStep && processingState === 'error' && (
                        <Typography variant="body2" color="error.main">
                          ✗ Failed: {error}
                        </Typography>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        {/* Processing Status & Results */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Processing Status
              </Typography>
              
              <Box mb={2}>
                <Chip
                  label={processingState === 'idle' ? 'Ready to Process' :
                         processingState === 'processing' ? 'Processing...' :
                         processingState === 'completed' ? 'Completed' : 'Error'}
                  color={processingState === 'idle' ? 'default' :
                         processingState === 'processing' ? 'primary' :
                         processingState === 'completed' ? 'success' : 'error'}
                  icon={processingState === 'idle' ? <InfoIcon /> :
                        processingState === 'processing' ? <ProcessIcon /> :
                        processingState === 'completed' ? <SuccessIcon /> : <ErrorIcon />}
                />
              </Box>

              {processingState === 'processing' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Processing in progress... Please wait while we analyze your document.
                </Alert>
              )}

              {processingState === 'completed' && results && (
                <Box>
                  <Alert 
                    severity={validationStatus && !validationStatus.isValid ? "warning" : "success"} 
                    sx={{ mb: 2 }}
                  >
                    {validationStatus && !validationStatus.isValid 
                      ? "Processing completed with validation issues" 
                      : "Processing completed successfully!"}
                  </Alert>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Processing Results:
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Document ID: {results.document_id || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Status: {results.status || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Processing Time: {results.processing_time || 'N/A'}
                    </Typography>
                  </Box>

                  {/* Validation Status */}
                  {validationStatus && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Validation Status:
                      </Typography>
                      <Chip
                        label={validationStatus.isValid ? 'Valid' : 'Invalid'}
                        color={validationStatus.isValid ? 'success' : 'error'}
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {validationStatus.message}
                      </Typography>
                      
                      {/* Confidence Score */}
                      {validationStatus.confidenceScore > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            LLM Confidence: {(validationStatus.confidenceScore * 100).toFixed(1)}%
                            <Chip
                              label={validationStatus.confidenceScore >= 0.9 ? 'Good' : 'Low'}
                              color={validationStatus.confidenceScore >= 0.9 ? 'success' : 'error'}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </Typography>
                        </Box>
                      )}
                      
                      {/* PO Validation */}
                      {validationStatus.poValidationStatus && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            PO Validation: 
                            <Chip
                              label={validationStatus.poValidationStatus === 'valid' ? 'Valid' : 'Invalid'}
                              color={validationStatus.poValidationStatus === 'valid' ? 'success' : 'error'}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </Typography>
                        </Box>
                      )}
                      
                      {validationStatus.failedChecks.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="error.main">
                            Failed Checks: {validationStatus.failedChecks.join(', ')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {validationStatus && validationStatus.processingSummary && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Extracted Data:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Invoice Number: {validationStatus.processingSummary.invoice_number || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Amount: {validationStatus.processingSummary.total_amount ? `$${validationStatus.processingSummary.total_amount}` : 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Supplier: {validationStatus.processingSummary.supplier_id || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        PO Number: {validationStatus.processingSummary.po_number || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Final Basket: {validationStatus.processingSummary.routed_to || 'N/A'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {processingState === 'error' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Processing failed: {error}
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Document Info */}
          {documentInfo && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Document Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  File: {documentInfo.metadata?.original_filename || documentInfo.document_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Size: {documentInfo.metadata?.file_size ? `${documentInfo.metadata.file_size} bytes` : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Uploaded: {documentInfo.metadata?.upload_timestamp ? 
                    new Date(documentInfo.metadata.upload_timestamp).toLocaleString() : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProcessingPage;
